import React, { FC, useContext } from 'react';
import { Card, Stack } from '@fiverr-private/layout_components';
import { Heading, Text, Typography } from '@fiverr-private/typography';
import { Image } from '@fiverr-private/media';
import { Button } from '@fiverr-private/button';
import { EXPERT_BANNER_TYPE, tracker } from '@fiverr-private/listing_lib';
import { useImpressionItem } from '@fiverr-private/impressionable';
import pathfinder from '@fiverr-private/pathfinder';
import { I18n } from '@fiverr-private/i18n-react';
import { BI_EVENT_TYPES, mixPanelBasePayload } from '../util';
import { AppContext } from '../../../context/listingsContext';
import { getWindow } from '../../../../../../shared/utils';
import { getImpressionEventPayload, reportBigQueryBannerClick } from '../bqReports';
import { MAX_ITEMS_TO_SHOW_NO_RESULTS } from '../../../../constants';
import { getBqEventSource } from '../../biUtils';
import { CUSTOM_CATEGORIES_SOURCE, CUSTOM_HREF_CATEGORIES, CUSTOM_TITLE_CATEGORIES } from './constants';
import styles from './styles.module.scss';

const I18N_PREFIX = 'search_perseus.business_listings.banners.project_manage';
const IMAGE_URL_1X =
    'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/83978aff7ad7bb1f352f5e423f56a308-1753860144855/managers_1x.png';
const IMAGE_URL_2X =
    'https://fiverr-res.cloudinary.com/image/upload/f_auto,q_auto/v1/attachments/generic_asset/asset/83978aff7ad7bb1f352f5e423f56a308-1753860144863/managers_2x.png';

interface ProjectManageBannerProps {
    resultsCount: number;
    hasActiveFilters: boolean;
    isFullCatalog?: boolean;
    position?: number;
    userData?: {
        role: string;
    };
}

const ProjectManageBanner: FC<ProjectManageBannerProps> = ({
    resultsCount,
    hasActiveFilters,
    position,
    userData,
    isFullCatalog = false,
}) => {
    const { isExpertListings = false, subCategoryData } = useContext(AppContext);
    const customTitle = CUSTOM_TITLE_CATEGORIES[subCategoryData?.category?.id];
    const customSource = CUSTOM_CATEGORIES_SOURCE[subCategoryData?.category?.id];
    const customHref = CUSTOM_HREF_CATEGORIES[subCategoryData?.category?.id];
    const BQEnrichment = getImpressionEventPayload(
        isExpertListings,
        resultsCount,
        hasActiveFilters,
        position,
        EXPERT_BANNER_TYPE.PROJECT_PARTNERS
    );
    const [mainContainerRef] = useImpressionItem({
        enrichment: BQEnrichment,
        onImpression: () => {
            tracker.trackBannerImpression(
                EXPERT_BANNER_TYPE.PROJECT_PARTNERS,
                mixPanelBasePayload(userData?.role, isExpertListings)
            );
        },
    });

    const mixPanelPayload = () => {
        const noResults = 'no results';
        const fewResults = 'less than 4 results';
        const regular = 'listing banner';
        let component;
        if (resultsCount === 0) {
            component = noResults;
        } else if (resultsCount <= MAX_ITEMS_TO_SHOW_NO_RESULTS) {
            component = fewResults;
        } else {
            component = regular;
        }
        return {
            ...mixPanelBasePayload(userData?.role, isExpertListings),
            component,
        };
    };

    const openProjectPartner = () => {
        reportBigQueryBannerClick(
            BI_EVENT_TYPES.CLICKED_ON_FIVERR_PRO_FIND_PROJECT_PARTNER,
            isExpertListings,
            resultsCount,
            hasActiveFilters,
            position
        );

        tracker.trackProjectPartnersImpression(mixPanelPayload());
        const proOrFullListingIndex = Number(isExpertListings);
        const generalEventSource = getBqEventSource(isExpertListings);
        const source = customSource ? customSource[proOrFullListingIndex] : generalEventSource;
        const pageSlug = customHref || 'project-management';
        const href = pathfinder('content_pages_page', { page_slug: pageSlug }, { query: { source } });
        getWindow()?.open(href, '_blank');
    };

    return (
        <Card
            borderRadius="xl"
            borderColor="lightOrange_200"
            background="linear-gradient(93deg, #FFFFFF 25%, #F3F6D4 85%, #D0E500 131%)"
            padding="6"
            minWidth="270px"
            className={isFullCatalog ? styles.fullCatalogContainer : ''}
            ref={mainContainerRef}
        >
            <Stack direction="row" rowGap="9" columnGap="9" justifyContent="spaceBetween">
                <Stack direction="column" gap="5" padding="2" paddingRight="0" alignItems="flexStart">
                    <Heading as="h5" data-testid="project-manage-banner-title">
                        {customTitle ? (
                            <I18n k={`${I18N_PREFIX}.title_by_category.${customTitle}`} />
                        ) : (
                            <I18n k={`${I18N_PREFIX}.title`} />
                        )}{' '}
                        <Typography as="span" color="green_1000" fontWeight="inherit" whiteSpace="nowrap">
                            <I18n k={`${I18N_PREFIX}.title_highlight`} />
                        </Typography>
                    </Heading>
                    <Text as="p" maxWidth="630px" textAlign="start">
                        <I18n k={`${I18N_PREFIX}.description`} />
                    </Text>
                    <Button size="sm" onClick={openProjectPartner} data-testid="project-manage-banner-button">
                        <I18n k={`${I18N_PREFIX}.button`} />
                    </Button>
                </Stack>
                <Stack
                    hidden={{ default: true, md: false }}
                    alignItems="center"
                    style={{ filter: 'drop-shadow(0 24px 30px rgba(66, 72, 0, 0.2))' }}
                >
                    <Image
                        src={IMAGE_URL_1X}
                        srcSet={`${IMAGE_URL_2X} 2x`}
                        alt="Fiverr Pro Project Managers"
                        width={{ default: '262px', md: '328px' }}
                    />
                </Stack>
            </Stack>
        </Card>
    );
};

export default ProjectManageBanner;
